// Office Messages page (Session ACZ): one-to-one internal messaging with each engineer. The left
// pane lists engineer threads (last-message preview + unread pill); the right pane is the open
// conversation with a compose box and an optional job link. Mirrors what the engineer sees in the
// PWA. Office/admin only (the route is gated). Follows the ACG office design system.
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import type { MessageThreadSummary, MessageThread, MessageDto, MessageJobOption } from "../../lib/api";
import { ConfirmButton } from "../../components/ConfirmButton";
import { isPushSupported, getExistingSubscription, subscribeToPush } from "../../lib/pushClient";

// A short, human timestamp for a message / thread row.
function whenLabel(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }) +
        " " +
        d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

const inputCls =
  "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";

export function MessagesPage() {
  const [threads, setThreads] = useState<MessageThreadSummary[]>([]);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [threadsError, setThreadsError] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // `background` for the polls (Session ASB review, F4): a timer's requests never keep the office session alive.
  const loadThreads = useCallback(async (background = false) => {
    try {
      const list = await api.messages.threads({ background });
      setThreads(list);
      setThreadsError(false);
    } catch {
      setThreadsError(true);
    } finally {
      setLoadingThreads(false);
    }
  }, []);

  useEffect(() => {
    void loadThreads();
  }, [loadThreads]);

  // Light poll so a new inbound message shows without a manual refresh (no websockets).
  useEffect(() => {
    const t = setInterval(() => void loadThreads(true), 20000);
    return () => clearInterval(t);
  }, [loadThreads]);

  const selectThread = useCallback((engineerUserId: string) => {
    setSelectedId(engineerUserId);
  }, []);

  return (
    <div>
      <div className="mb-4 max-sm:flex-col max-sm:items-start flex items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl tracking-wide text-ele-strong">Messages</h1>
          <p className="mt-0.5 text-sm text-ele-body">Message your engineers directly. They see and reply from their app.</p>
        </div>
        <OfficePushToggle />
      </div>

      <div className="grid gap-4 md:grid-cols-[minmax(220px,300px)_1fr]">
        {/* Thread list */}
        <div className={`${selectedId ? "hidden md:block" : "block"}`}>
          <ThreadList
            threads={threads}
            loading={loadingThreads}
            error={threadsError}
            selectedId={selectedId}
            onSelect={selectThread}
          />
        </div>

        {/* Open conversation */}
        <div className={`${selectedId ? "block" : "hidden md:block"}`}>
          {selectedId ? (
            <ThreadPanel
              key={selectedId}
              engineerUserId={selectedId}
              onBack={() => setSelectedId(null)}
              onChanged={loadThreads}
            />
          ) : (
            <div className="flex h-full min-h-[240px] items-center justify-center rounded-ele border border-ele-purple-accent bg-ele-surface p-8 text-center text-sm text-ele-body">
              Choose an engineer on the left to open the conversation.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// A quiet header affordance to turn on desktop notifications for this browser (Session AFF). Shown
// only when push is supported, the server has VAPID keys, and this browser is not already
// subscribed; on success it swaps to a muted "on" label.
function OfficePushToggle() {
  const [supported] = useState(() => isPushSupported());
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isPushSupported()) return;
      let status;
      try {
        status = await api.push.status();
      } catch {
        return;
      }
      if (cancelled || !status.configured || !status.publicKey) return;
      setPublicKey(status.publicKey);
      const existing = await getExistingSubscription();
      if (cancelled) return;
      setSubscribed(Boolean(existing));
    })().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const enable = useCallback(async () => {
    if (busy || !publicKey) return;
    setBusy(true);
    const result = await subscribeToPush(publicKey);
    setBusy(false);
    if (result.ok) setSubscribed(true);
  }, [busy, publicKey]);

  if (!supported || !publicKey || subscribed === null) return null;
  if (subscribed) return <span className="text-xs text-ele-body">Desktop notifications on</span>;
  return (
    <button
      type="button"
      onClick={() => void enable()}
      disabled={busy}
      className="text-sm text-ele-green hover:underline disabled:opacity-40"
    >
      {busy ? "Enabling..." : "Enable desktop notifications"}
    </button>
  );
}

function ThreadList({
  threads,
  loading,
  error,
  selectedId,
  onSelect,
}: {
  threads: MessageThreadSummary[];
  loading: boolean;
  error: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  if (loading) return <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4 text-sm text-ele-body">Loading...</div>;
  if (error) return <div className="rounded-ele border border-ele-danger/40 bg-ele-surface p-4 text-sm text-ele-danger">Could not load messages.</div>;
  if (threads.length === 0)
    return <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4 text-sm text-ele-body">No engineers to message yet.</div>;

  return (
    <ul className="space-y-1.5">
      {threads.map((t) => {
        const active = t.engineerUserId === selectedId;
        return (
          <li key={t.engineerUserId}>
            <button
              type="button"
              onClick={() => onSelect(t.engineerUserId)}
              className={`w-full rounded-ele border px-3 py-2.5 text-left transition ${
                active ? "border-ele-green bg-[rgba(99,196,31,0.07)]" : "border-ele-purple-accent bg-ele-surface hover:border-ele-green/50"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate font-heading text-sm text-ele-strong">{t.displayName}</span>
                {t.unreadCount > 0 && (
                  <span className="ele-pill ele-pill--green flex-none" title={`${t.unreadCount} unread`}>
                    {t.unreadCount}
                  </span>
                )}
              </div>
              <div className="mt-0.5 flex items-center justify-between gap-2">
                <span className="truncate text-xs text-ele-body">
                  {t.lastMessagePreview ? (
                    <>
                      {t.lastMessageDirection === "to_engineer" && <span className="text-ele-body/60">You: </span>}
                      {t.lastMessagePreview}
                    </>
                  ) : (
                    <span className="italic opacity-60">No messages yet</span>
                  )}
                </span>
                <span className="flex-none text-[10px] uppercase tracking-wide text-ele-body/60">{whenLabel(t.lastMessageAt)}</span>
              </div>
              {t.techId && <div className="mt-0.5 font-menu text-[10px] uppercase tracking-wide text-ele-body/50">{t.techId}</div>}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function ThreadPanel({
  engineerUserId,
  onBack,
  onChanged,
}: {
  engineerUserId: string;
  onBack: () => void;
  onChanged: (background?: boolean) => void;
}) {
  const [thread, setThread] = useState<MessageThread | null>(null);
  const [jobs, setJobs] = useState<MessageJobOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [body, setBody] = useState("");
  const [jobId, setJobId] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const load = useCallback(
    async (markRead: boolean, background = false) => {
      try {
        const t = await api.messages.thread(engineerUserId, { background });
        setThread(t);
        setError(false);
        if (markRead) {
          // Clear unread for this thread, then refresh the list badge.
          await api.messages.markRead(engineerUserId, { background }).catch(() => undefined);
          onChanged(background);
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [engineerUserId, onChanged],
  );

  // Initial load marks the thread read; the poll below does not (so it does not fight the badge).
  useEffect(() => {
    void load(true);
    api.messages.threadJobs(engineerUserId).then(setJobs).catch(() => setJobs([]));
  }, [engineerUserId, load]);

  // Poll while the thread is open. Mark read on each poll so a message that arrives while the office
  // is looking at the conversation is treated as read (standard chat behaviour), keeping the badge
  // honest; markRead is a no-op when nothing new has arrived.
  useEffect(() => {
    const t = setInterval(() => void load(true, true), 15000);
    return () => clearInterval(t);
  }, [load]);

  // Keep the newest message in view.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [thread?.messages.length]);

  async function send() {
    const text = body.trim();
    if (!text || sending) return;
    const to = thread?.sendToUserId;
    if (!to) {
      setSendError("No recipient available for this thread.");
      return;
    }
    setSending(true);
    setSendError(null);
    try {
      await api.messages.send({ toUserId: to, body: text, jobId: jobId || undefined });
      setBody("");
      setJobId("");
      await load(true);
    } catch (e) {
      setSendError(e instanceof Error ? e.message : "Could not send the message.");
    } finally {
      setSending(false);
    }
  }

  async function remove(id: string) {
    try {
      await api.messages.remove(id);
      await load(false);
      onChanged();
    } catch {
      setSendError("Could not delete that message.");
    }
  }

  return (
    <div className="flex h-full min-h-[60vh] flex-col rounded-ele border border-ele-purple-accent bg-ele-surface">
      {/* Header */}
      <div className="flex flex-none items-center gap-2 border-b border-ele-purple-accent px-4 py-3">
        <button type="button" onClick={onBack} className="rounded-ele border border-ele-purple-accent px-2 py-1 text-xs text-ele-green md:hidden" aria-label="Back to threads">
          ‹ Back
        </button>
        <div className="min-w-0">
          <div className="truncate font-heading text-lg text-ele-strong">{thread?.engineer.displayName ?? "Conversation"}</div>
          {thread?.engineer.techId && <div className="font-menu text-[10px] uppercase tracking-wide text-ele-body/60">{thread.engineer.techId}</div>}
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto px-4 py-4">
        {loading ? (
          <p className="text-sm text-ele-body">Loading...</p>
        ) : error ? (
          <p className="text-sm text-ele-danger">Could not load this conversation.</p>
        ) : thread && thread.messages.length === 0 ? (
          <p className="py-8 text-center text-sm text-ele-body">No messages yet. Say hello below.</p>
        ) : (
          thread?.messages.map((msg) => <OfficeBubble key={msg.id} msg={msg} onDelete={() => remove(msg.id)} />)
        )}
      </div>

      {/* Compose */}
      <div className="flex-none border-t border-ele-purple-accent p-3">
        {jobs.length > 0 && (
          <select value={jobId} onChange={(e) => setJobId(e.target.value)} className={`${inputCls} mb-2`} aria-label="Link a job (optional)">
            <option value="">Link a job (optional)</option>
            {jobs.map((j) => (
              <option key={j.id} value={j.id}>
                {j.label}
              </option>
            ))}
          </select>
        )}
        <div className="flex items-end gap-2">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                void send();
              }
            }}
            rows={2}
            maxLength={2000}
            placeholder="Type a message..."
            className={`${inputCls} resize-none`}
          />
          <button
            type="button"
            onClick={() => void send()}
            disabled={sending || body.trim().length === 0}
            className="flex-none rounded-ele bg-ele-green px-4 py-2 font-menu text-sm font-semibold tracking-wide text-ele-bg transition hover:brightness-110 disabled:opacity-40"
          >
            {sending ? "Sending..." : "Send"}
          </button>
        </div>
        {sendError && <p className="mt-1.5 text-xs text-ele-danger">{sendError}</p>}
        <p className="mt-1 text-[10px] text-ele-body/50">Ctrl/Cmd + Enter to send.</p>
      </div>
    </div>
  );
}

function OfficeBubble({ msg, onDelete }: { msg: MessageDto; onDelete: () => void }) {
  // In the office view, a message the office sent (to_engineer) is outbound (right, green tint); an
  // engineer's message (to_office) is inbound (left, neutral).
  const outbound = msg.direction === "to_engineer";
  return (
    <div className={`group flex ${outbound ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[78%] rounded-ele border px-3 py-2 ${outbound ? "border-ele-green/30 bg-[rgba(99,196,31,0.08)]" : "border-ele-purple-accent bg-ele-inset"}`}>
        {!outbound && msg.fromUserName && <div className="mb-0.5 font-menu text-[10px] uppercase tracking-wide text-ele-body/60">{msg.fromUserName}</div>}
        <div className="whitespace-pre-wrap break-words text-sm text-ele-strong">{msg.body}</div>
        {msg.jobNumber && <div className="mt-1 text-[11px] text-ele-blue">Job #{msg.jobNumber}</div>}
        <div className="mt-1 flex items-center justify-end gap-2">
          <span className="text-[10px] text-ele-body/50">{whenLabel(msg.createdAt)}</span>
          <span className="opacity-0 transition group-hover:opacity-100">
            <ConfirmButton onConfirm={onDelete} label="Delete" className="text-[10px] tracking-wide text-ele-body/60 hover:text-ele-danger" />
          </span>
        </div>
      </div>
    </div>
  );
}
